import { trpc } from '@viastud/ui/lib/trpc'
import { useCallback, useState } from 'react'

import { useAuthStore } from '@/store/auth.store.ts'

export interface Child {
  id: string
  firstName: string
  lastName: string
  status:
    'active' | 'suspended' | 'cancelled' | 'expired' | 'pending' | 'past_due' | 'inactive' | 'ended'
  endDate?: string | null
  nextBilling?: string | null
  isSubscribed?: boolean
  commitment: boolean
  commitmentEndDate: string | null
}

const SUBSCRIPTIONS = {
  WITH_COMMITMENT: 4,
  NO_COMMITMENT: 3,
}

export function useParentSubscriptionPresenter() {
  const { user: parent } = useAuthStore()

  const [selectedChild, setSelectedChild] = useState<Child | null>(null)
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [subscribeOpen, setSubscribeOpen] = useState(false)

  // Queries
  const {
    data: subscriptionData,
    refetch,
    isLoading,
  } = trpc.user.getParentSubscriptionDetails.useQuery()
  const { data: payments = [], isLoading: paymentsLoading } = trpc.user.getUserPayments.useQuery()
  const { data: invoices = [], isLoading: invoicesLoading } = trpc.user.getUserInvoices.useQuery()

  // Mutations
  const subscribeMutation = trpc.payment.createSubscriptionIntent.useMutation({
    onSuccess: () => refetch(),
  })
  /*const subscribeChildMutation = trpc.payment.subscribeForChild.useMutation({
    onSuccess: () => refetch(),
  })*/
  const cancelMutation = trpc.payment.cancelSubscriptionForChild.useMutation({
    onSuccess: () => refetch(),
  })
  const renewMutation = trpc.payment.renewSubscriptionForChild.useMutation({
    onSuccess: () => refetch(),
  })
  const downloadMutation = trpc.user.downloadInvoice.useMutation({
    onSuccess: (data: { pdfUrl: string }) => window.open(data.pdfUrl, '_blank'),
  })

  // Simplified Actions
  const handleSubscribe = useCallback(
    async (commitment: boolean, childId: string) => {
      try {
        if (!parent) {
          return
        }

        const result = await subscribeMutation.mutateAsync({
          role: 'PARENT',
          planId: commitment ? SUBSCRIPTIONS.WITH_COMMITMENT : SUBSCRIPTIONS.NO_COMMITMENT,
          childId: childId,
          commitment,
        })

        setClientSecret(result.clientSecret)
        setSubscribeOpen(true)
      } catch (error) {
        console.error('Subscription failed', error)
      }
    },
    [parent, subscribeMutation]
  )

  const handleCancel = useCallback(
    async (childId: string) => {
      try {
        await cancelMutation.mutateAsync({ childId })
      } catch (error) {
        console.error('Cancellation failed', error)
      }
    },
    [cancelMutation]
  )

  const handleRenew = useCallback(
    async (childId: string) => {
      try {
        await renewMutation.mutateAsync({ childId, selectedPlan: 1 })
      } catch (error) {
        console.error('Renewal failed', error)
      }
    },
    [renewMutation]
  )

  const handleDownloadInvoice = useCallback(
    (invoiceId: string) => {
      downloadMutation.mutate({ stripeInvoiceId: invoiceId })
    },
    [downloadMutation]
  )

  return {
    // Data
    children: subscriptionData?.children ?? [],
    subscriptionData,
    payments,
    invoices,

    clientSecret,
    subscribeOpen,
    setSubscribeOpen,

    // States
    isLoading: isLoading || paymentsLoading || invoicesLoading,
    isActionLoading:
      subscribeMutation.isPending || cancelMutation.isPending || renewMutation.isPending,
    isDownloading: downloadMutation.isPending,
    selectedChild,

    // Setters
    setSelectedChild,

    // Simplified Business Logic
    handleSubscribe,
    handleCancel,
    handleRenew,
    handleDownloadInvoice,
    refetch,
  }
}
